import {
  Text,
  View,
  TextInput,
  Pressable,
  Switch,
  ImageBackground,
  TouchableOpacity,
  Keyboard,
  Linking,
} from 'react-native'
import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { ImageSourcePropType } from 'react-native'

import { SvgComponent } from '../../components/Svg/Svg.component';
import { Password } from '../Password';

import { loadSession, onLogin, loginWithBiometrics } from './Connexion.actions'
import { ConnexionProps } from './Connexion.types';
import styles from './Connexion.styles';
import { useNotify } from '../../components/Notification';

import mairistem from '../../assets/svg/mairistem.svg';
import background from '../../assets/images/esag.png';
import Logo from '../../assets/svg/logo.svg';
import { stdCouleurBleu, stdCouleurGris, switchStyle, stdCouleurBleuClair, isWeb } from '@commonStyle';
import { useResponsive } from '@hooks';
import { isBiometricLoginEnabled, useBiometricAutoLoginStore } from '../../lib/Biometrics';

const CLUF_URL = 'https://espaceagent360.jvsonline.fr/privacy-policy.html';

const defaultSession = {
  username: '',
  password: '',
  rememberMe: false,
};

export const Connexion: FunctionComponent<ConnexionProps> = ({ navigation }: any) => {
  const [session, setSession] = useState(defaultSession);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const hasAutoTriggeredBiometric = useRef(false);
  const notify = useNotify();
  const isResponsive = useResponsive();

  useEffect(() => {
    const fetchSession = async () => {
      const sessionData = await loadSession();
      if (sessionData) {
        setSession(sessionData);
      }
      // Si pas de sessionData, on garde les valeurs par défaut déjà définies
    };
    fetchSession();
  }, [setSession]); // Ajout de setSession dans les dépendances pour éviter l'avertissement

  useEffect(() => {
    isBiometricLoginEnabled().then(setBiometricEnabled);
  }, []);

  const handleBiometricLogin = useCallback(() => {
    loginWithBiometrics(navigation, notify);
  }, [navigation, notify]);

  useEffect(() => {
    // Dès que la biométrie est activée, on tente directement la connexion,
    // sans passer par un bouton dédié. En cas d'échec/annulation, le
    // formulaire identifiant/mot de passe reste disponible pour se connecter
    // normalement. Exception : juste après une déconnexion explicite, on ne
    // relance pas automatiquement la biométrie (sinon "Se déconnecter" ne
    // déconnecte pas vraiment) — ce flag est à usage unique.
    if (biometricEnabled && !hasAutoTriggeredBiometric.current) {
      hasAutoTriggeredBiometric.current = true;
      if (!useBiometricAutoLoginStore.getState().consumeSuppression()) {
        handleBiometricLogin();
      }
    }
  }, [biometricEnabled, handleBiometricLogin]);

  const handleOpenCluf = async () => {
    if (isWeb) {
      window.open(CLUF_URL, '_blank');
    } else {
      const supported = await Linking.canOpenURL(CLUF_URL);
      if (supported) {
        await Linking.openURL(CLUF_URL);
      }
    }
  };

  return (
    <View style={styles.container}>
      {!isResponsive && (
        <View style={styles.background}>
          <ImageBackground
            source={background as ImageSourcePropType}
            resizeMethod="scale"
            style={styles.backgroundImage}
          />
        </View>
      )}
      
      {showPasswordReset ? (
        <Password onBack={() => setShowPasswordReset(false)} />
      ) : (
        <View style={styles.mainContainer}>
          <View style={[styles.logoContainer]}>
            <SvgComponent svgFile={Logo} width={120} height={100} />          
          </View>
          <View style={[styles.formContainer]}>
          
            <View>
              <TextInput
                style={styles.inputLogin}
                underlineColorAndroid="transparent"
                placeholder="Identifiant"
                placeholderTextColor="#115688"
                autoCapitalize="none"
                value={session?.username || ''}
                onChangeText={(value) => setSession({ ...session, username: value })}
                testID='SAI_LOGIN'
              />
              <View style={styles.passwordContainer}>
                <TextInput
                  style={[styles.inputLogin, styles.passwordInput]}
                  underlineColorAndroid="transparent"
                  placeholder="Mot de passe"
                  placeholderTextColor="#115688"
                  autoCapitalize="none"
                  secureTextEntry={!showPassword}
                  value={session?.password || ''}
                  onChangeText={(value) => setSession({ ...session, password: value })}
                  onSubmitEditing={() => { Keyboard.dismiss(); onLogin(session.username, session.password, session.rememberMe, navigation, notify); }}
                  returnKeyType="go"
                  testID='SAI_PASSWORD'
                />
                <TouchableOpacity onPress={() => setShowPassword(v => !v)} testID='BT_TOGGLE_PASSWORD'>
                  <MaterialCommunityIcons name={showPassword ? 'eye-off' : 'eye'} size={22} color="#115688" />
                </TouchableOpacity>
              </View>
              <Pressable style={styles.buttonBleu}
                onPress={() => { Keyboard.dismiss(); onLogin(session.username, session.password, session.rememberMe, navigation, notify); }}
                testID='BT_LOGIN'>
                <Text style={styles.buttonText}>Se connecter</Text>
              </Pressable>
            </View>

            <View style={styles.sectionContainer}>
            <View style={styles.centeredRowContainer}>
              <Switch
                style={switchStyle}
                trackColor={{ false: stdCouleurGris, true: stdCouleurBleuClair }}
                thumbColor={stdCouleurBleu}
                ios_backgroundColor={stdCouleurGris}
                value={session.rememberMe}
                onValueChange={(value) => setSession({ ...session, rememberMe: value })}
                testID='INT_REMEMBER_ME'
              />
              <Pressable onPress={() => setSession({ ...session, rememberMe: !session.rememberMe })} testID='BT_REMEMBER_ME'>
                <Text style={styles.linkText}>Se souvenir de moi</Text>
              </Pressable>
            </View>
            </View>

            <Pressable onPress={() => setShowPasswordReset(true)} style={styles.mdpContainer} testID='BT_PASSWORD_RESET'>
              <Text style={[styles.linkText, styles.switchLabelText]}>Mot de passe oublié</Text>
            </Pressable>
          </View>

          <View style={styles.bottomContainer}>
          <SvgComponent svgFile={mairistem} width={200} height={100} fill={'#000000'} />
          <Pressable onPress={handleOpenCluf} testID='BT_CLUF'>
            <Text style={styles.clufLink}>Conditions générales d'utilisation (CLUF)</Text>
          </Pressable>
        </View>
      </View>
      )}
    </View>
  );
}